import React, { useMemo, useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  InputGroup
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import TableContainerV1 from "components/Common/TableContainerV1"
import { getDealerInquiryList } from "helpers/UAM/inquiry.helper"
import moment from "moment"
import Flatpickr from "react-flatpickr";
import { formatNumberWithCommas } from '../../common/utility'

const ListProductInquiry = () => {
  document.title="Product Inquiry | DMS"
  const navigate = useNavigate();

  const [inquiryList, setInquiryList] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const flatpickrRef = useRef(null);

  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  });


  const getInquiry = async (bodyData) => {
    const response = await getDealerInquiryList(bodyData);
    if (response.status) {
      setInquiryList(response.data.data);
      const { totalItems, totalPages, currentPage } = response.data
      setPagination({
        pageIndex: currentPage,
        pageCount: totalPages,
        totalRecords: totalItems,
      })
    } else {
      setInquiryList([])
      setPagination({
        pageIndex: 0,
        pageCount: 0,
        totalRecords: 0,
      })
    }
  }

  useEffect(() => {
    const bodyData = {
      filterType: filters,
      page: currentPage,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    getInquiry(bodyData);
  }, [searchValue, pageSize, columnId, sortingOrderStatus, currentPage]);


  const handlePageChange = (page, pageSize) => {
    setPageSize(pageSize);
    setCurrentPage(page);
  }

  const handleSortColumn = (option) => {
    setSortingOptions(option);
    const columnName = option.column;
    setColumnId(columnName);
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder);
  };


  const handleSearchFilter = (value) => {
    setCurrentPage(1)
    setSearchValue(value);
  };
  const handleClearSearch = () => {
    setSearchValue("");
    setFilters({
      startDate: '',
      endDate: '',
      status: ''
    })
    flatpickrRef.current.flatpickr.clear();
    let bodyData = {
      filterType: {
        startDate: '',
        endDate: '',
        status: ''
      },
      page: currentPage,
      searchKey: "",
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }

    getInquiry(bodyData);
  };


  const ProductDescription = ({ value }) => {
    if (value && value.length > 50) {
      let newValue = value.substring(0, 50) + '...';
      return (
        <span className="d-inline" tabIndex="0" data-toggle="tooltip" title={value} dir="top" data-placement="top">
         {newValue}
        </span>
      )
    }
    return (
      <span>{value}</span>
    )
  }


  const columns = useMemo(
    () => [
      {
        Header: "Inquiry Number",
        accessor: "inquiryNumber",
        Cell: cellProps => {
          return cellProps.value ? `INQ-${cellProps.value}` : ""
        },
      },
      {
        Header: "Sub Inquiry Number",
        accessor: "subinquiryNumber",
        Cell: cellProps => {
          return cellProps.value ? `SUBINQ-${cellProps.value}` : ""
        },
      },
      {
        Header: "Product Description",
        accessor: "productDescription",
        width: "100px",
        filterable: true,
        Cell: cellProps => {
          return <ProductDescription value={cellProps.value} />
        },
      },
      {
        Header: "Status",
        accessor: "productStatus",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value == 'pending' ? 'Submitted' : 'Closed'
        },
      },
      {
        Header: "Created At",
        accessor: "createdAt",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? moment(cellProps.value).format("MMM DD, YYYY HH:mm A") : ""
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  onClick={() => {
                    const edit = cellProps.row.original
                    handleView(edit)
                  }}
                >
                  <i
                    className="mdi mdi-eye font-size-16 text-success me-1"
                    id="edittooltip"
                  ></i>
                  View
                  <UncontrolledTooltip placement="top" target="edittooltip">
                    View
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )
        },
      },
    ],
    []
  )


  const handleView = arg => {
    const subinquiryNumber = arg.subinquiryNumber;
    const enCodedId = btoa(subinquiryNumber)
    navigate(`/customer/product-inquiry/view/${enCodedId}`);
  }

  const handleDateFilter = () => {
    const bodyData = {
      filterType: filters,
      page: 1,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    setCurrentPage(1)
    getInquiry(bodyData);
  }

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setFilters({
        ...filters,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
      })
    }
  };

  const customeFilterSection = (
    <div className="d-flex align-items-center justify-content-start">
      <InputGroup style={{ minWidth: '250px' }}>
        <Flatpickr
          ref={flatpickrRef}
          className="form-control"
          placeholder="Start Date - End Date"
          options={{
            mode: 'range',
            dateFormat: 'd-m-Y'
          }}
          value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
          onChange={handleDateChange}
        />
        <Button variant="primary"
          onClick={handleDateFilter}
          style={{ backgroundColor: "#344fa0", border: "none" }}>
          Go
        </Button>
      </InputGroup>
    </div>
  )


  const buttonSection = (
    <div className="text-sm-end">
      {
        <Button
          type="button"
          color="success"
          className="btn-rounded mb-2 me-2"
          onClick={() => {
            navigate("/customer/product-inquiry/add");
          }}
        >
          <i className="mdi mdi-plus me-1" />
          Add Inquiry
        </Button>
      }
    </div>
  )


  return (
      <div className="page-content">
        <Container fluid>
          {/* <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>Inquiry List</h4>
            </div> */}
            <Breadcrumbs breadcrumbItem="Inquiry List" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                <TableContainerV1
                  columns={columns}
                  searchHelperText="Search by Inquiry Number, Sub Inquiry Number, Product Description"
                  data={inquiryList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  customFilterSection={customeFilterSection}
                  customSection={buttonSection}
                  handleSearchFilter={handleSearchFilter}
                  sortOptions={sortingOptions}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ListProductInquiry
